import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { injectIntl, intlShape } from 'react-intl';

import messages from './messages';

import {
    getMasterCoursesCountries,
    getMasterCoursesByCountry,
    getSubjects,
    changeCountry,
    changeSubject,
    filterMasterCourses,
    getMasterCourses
} from './actions';

import {
    YEAR_GROUPS,
    COURSE_CREATOR_ROUTE,
    DEFAULT_CURRICULUM_TYPE_ID,
    COURSE_CREATOR_SUB_ROUTES,
    MASTER_COURSES_ROUTE
} from '../../constants';

import CourseFilter from '../../components/course-filter';
import Header from '../../components/header';
import Container from '../../components/container';
import Button from '../../components/button';
import DropdownList from '../../components/dropdown-list';
import TextInput from '../../components/text-input';
import withLoader from '../../components/with-loader';
import CourseTypeSelector from '../../components/course-type-selector/course-type-selector';
import {
    DataTable,
    ActionRenderer,
    CellRenderer
} from '../../components/data-table';

class MasterCourses extends PureComponent {
    static propTypes = {
        intl: intlShape.isRequired,
        getMasterCoursesCountries: PropTypes.func,
        getMasterCoursesByCountry: PropTypes.func,
        getMasterCourses: PropTypes.func,
        getSubjects: PropTypes.func,
        changeSubject: PropTypes.func,
        masterCourses: PropTypes.array,
        filterMasterCourses: PropTypes.func,
        countries: PropTypes.oneOfType([
            PropTypes.object,
            PropTypes.array
        ]),
        filteredCourses: PropTypes.array,
        isCountryLoaded: PropTypes.bool,
        isMasterCoursesLoading: PropTypes.bool,
        isMasterCoursesLoaded: PropTypes.bool,
        isSubjectLoaded: PropTypes.bool,
        selectedCountry: PropTypes.string,
        searchFilter: PropTypes.object,
        subjects: PropTypes.oneOfType([
            PropTypes.object,
            PropTypes.array
        ])
    }

    static contextTypes = {
        router: PropTypes.object.isRequired
    };

    static defaultProps = {
        masterCourses: []
    };

    constructor(props) {
        super(props);
        this.cloneMasterCourse = this.cloneMasterCourse.bind(this);
        this.onTextSearch = this.onTextSearch.bind(this);
        this.onYearGroupChange = this.onYearGroupChange.bind(this);
        this.onSubjectChange = this.onSubjectChange.bind(this);
        this.onCountryChange = this.onCountryChange.bind(this);
        this.handleOnCourseTypeChange = this.handleOnCourseTypeChange.bind(this);

        this.state = {
            selectedCourseType: DEFAULT_CURRICULUM_TYPE_ID,
            selectedCountry: 'AUS'
        };

        this.courseTypeOptions = [
            { value: 'Curriculum type', key: -1 },
            { value: 'Global', key: 1 },
            { value: 'Country Specific', key: 0 }
        ];

        this.globalCourseId = 1;
        this.countryCourseId = 0;
    }

    componentDidMount() {
        const { isCountryLoaded, isSubjectLoaded } = this.props;
        if (!isSubjectLoaded) {
            this.props.getSubjects();
        }
        if (!isCountryLoaded) {
            this.props.getMasterCoursesCountries();
        }
        this.props.getMasterCourses(this.countryCourseId);
    }

    cloneMasterCourse({ subjectId, id, version }) {
        const { CLONE } = COURSE_CREATOR_SUB_ROUTES;
        this.context.router.history.push(`${COURSE_CREATOR_ROUTE}${MASTER_COURSES_ROUTE}/${CLONE}/${subjectId}/${id}/${version}`);
    }

    onTextSearch(searchTerm) {
        this.props.filterMasterCourses({ ...this.props.searchFilter, searchTerm });
    }

    onCountryChange(name, value) {
        this.setState({
            selectedCountry: value
        });
    }

    onYearGroupChange(field, yearGroup) {
        this.props.filterMasterCourses({ ...this.props.searchFilter, yearGroup });
    }

    onSubjectChange(name, subjectId) {
        this.props.filterMasterCourses({ ...this.props.searchFilter, subjectId });
    }

    handleOnCourseTypeChange = (value) => {
        this.props.getMasterCourses(value);
        this.setState({
            selectedCourseType: Number(value)
        });
    };

    render() {
        const { isMasterCoursesLoading } = this.props;
        const { selectedCourseType } = this.state;
        const dataTableMessage = isMasterCoursesLoading ? messages.loadingMessage : messages.emptyMessage;
        const emptyContent = (<div>
            {this.props.intl.formatMessage(dataTableMessage)}
        </div>);

        const headers = [
            {
                id: 'name',
                accessor: d => d.name,
                Header: this.props.intl.formatMessage(messages.curriculumLabel)
            },
            {
                id: 'Subject',
                accessor: d => d.subject,
                Header: <DropdownList
                    onChange={this.onSubjectChange}
                    optionKey="id"
                    options={this.props.subjects}
                    optionText="name"
                    value={this.props.searchFilter.subjectId} />,
                width: 150,
                Cell: CellRenderer,
                sortable: false
            },
            {
                id: 'yearGroupCombined',
                accessor: d => d.yearGroupCombined,
                Header: this.props.intl.formatMessage(messages.yearLabel),
                width: 100
            },
            {
                id: 'version',
                accessor: d => d.version,
                Header: this.props.intl.formatMessage(messages.versionLabel),
                width: 100
            },
            {
                id: 'countryCode',
                accessor: d => d.countryCode,
                Header: this.props.intl.formatMessage(messages.countryLabel),
                width: 100,
                Cell: CellRenderer
            },
            {
                id: 'state',
                accessor: d => d.state,
                Header: this.props.intl.formatMessage(messages.stateLabel),
                Cell: CellRenderer,
                width: 300
            },
            {
                id: 'action',
                accessor: d => d.action,
                Header: this.props.intl.formatMessage(messages.actionLabel),
                width: 150,
                sortable: false
            }
        ];
        const itemActions = item => (<Button
            handleOnClick={() => this.cloneMasterCourse(item)}
            inline
            label={this.props.intl.formatMessage(messages.copyLabel)}
            primary />);

        const shouldFilterCourses = this.state.selectedCourseType === DEFAULT_CURRICULUM_TYPE_ID || this.state.selectedCourseType === this.countryCourseId;

        const filteredCourses = this.props.filteredCourses.filter(
            (course => !shouldFilterCourses || course.countryCode === this.state.selectedCountry)
        ).map(
            item => ({
                ...item,
                action: (<ActionRenderer>{itemActions(item)}</ActionRenderer>)
            })
        );

        return (
            <div>
                <Header
                    subtitle={this.props.intl.formatMessage(messages.curriculumLabel)}
                    title={this.props.intl.formatMessage(messages.title)} />
                <Container>
                    <CourseFilter
                        {...this.props}>
                        <TextInput
                            clearText
                            disabled={isMasterCoursesLoading}
                            handleOnChange={this.onTextSearch} />
                        <CourseTypeSelector
                            onChange={this.handleOnCourseTypeChange}
                            options={this.courseTypeOptions}
                        />
                        <DropdownList
                            disabled={isMasterCoursesLoading || this.globalCourseId === selectedCourseType}
                            name="countryCode"
                            onChange={this.onCountryChange}
                            optionKey="code"
                            options={this.props.countries}
                            optionText="name"
                            value={this.state.selectedCountry} />
                        <DropdownList
                            disabled={isMasterCoursesLoading}
                            name="year"
                            onChange={this.onYearGroupChange}
                            optionKey="id"
                            options={YEAR_GROUPS}
                            placeholder={this.props.intl.formatMessage(messages.yearLabel)}
                            value={this.props.searchFilter.yearGroup} />

                    </CourseFilter>
                    <DataTable
                        className={'-striped'}
                        columns={headers}
                        data={filteredCourses}
                        defaultPageSize={50}
                        noDataText={emptyContent} />
                </Container>
            </div>
        );
    }
}

function mapStateToProps(state = {}) {
    return {
        ...state,
        countries: state.masterCourse.countries.items || [],
        isCountryLoaded: state.masterCourse.countries.isLoaded,
        fetching: state.masterCourse.isLoading,
        masterCourses: state.masterCourse.masterCourses,
        filteredCourses: state.masterCourse.filteredCourses,
        isMasterCoursesLoading: state.masterCourse.isLoading,
        isMasterCoursesLoaded: state.masterCourse.isLoaded,
        selectedCountry: state.masterCourse.selectedCountry,
        searchFilter: state.masterCourse.searchFilter,
        subjects: state.masterCourse.subjects.items || [],
        isSubjectLoaded: state.masterCourse.subjects.isLoaded
    };
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators({
        getMasterCoursesCountries,
        getMasterCoursesByCountry,
        getSubjects,
        changeCountry,
        changeSubject,
        filterMasterCourses,
        getMasterCourses
    }, dispatch);
}

const MasterCoursesWithLoader = withLoader('fetching')(MasterCourses);
export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(MasterCoursesWithLoader));




// WEBPACK FOOTER //
// ./app/containers/master-courses/index.js